import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {Button, Form, Select} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import {connect} from 'react-redux';
import {Collapse} from 'react-collapse';
import {StyledAddCardModal, StyledCardInfo, StyledAddCardModalFormSpace} from './StyledAddCardModal';
import EuroInput from '../../../../../EuroInput';
import SpinSmall from '../../../../../SpinSmall';
import IncreaseLimitAlert from '../../../../SubscriptionsPage/IncreaseLimitAlert';
import {ReactComponent as CardSvgImage} from '../../../../../../static/images/pages/subscriptions/card-template.svg';
import {cardsHelpers, CardLimitPeriodTypeOptions, formHelpers} from '../../../../../../utils/helpers';
import {StyledBudgetDetailsFormSpace} from '../BudgetDetails/StyledBudgetDetails';
import {subscriptionFormFields, subscriptionFormValues} from '../../../../../../constants';

const {Item} = Form;

const {
  defaultBudgetLimitSubscription,
  defaultCardLimitPeriod
} = subscriptionFormValues;

const {
  cardLimitFieldName,
  cardLimitPeriodFieldName
} = subscriptionFormFields;

const defaultFormValues = {
  [cardLimitFieldName]: defaultBudgetLimitSubscription,
  [cardLimitPeriodFieldName]: defaultCardLimitPeriod,
  card_owner: ''
}


const AddCardModal = ({
  dispatch,
  employees,
  employeeEmail,
  handleCancel,
  handleOk,
  loading = false,
  open = false,
  subscriptionOwner,
  subscriptionCardLimit,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'subscriptions']);
  const [form] = Form.useForm();
  const [initialFormValues, setInitialFormValues] = useState(defaultFormValues);
  const [cardLimitPeriodTypeOptions] = useState(CardLimitPeriodTypeOptions());
  const [ownerOptions, setOwnerOptions] = useState([]);

  const cardLimit = Form.useWatch(cardLimitFieldName, {form});

  const isTooHighLimit = useMemo(() => {
    let isHigh = false;
    if (cardLimit) {
      isHigh = cardsHelpers.isTooHighLimit({
        limit: cardLimit,
        maxLimit: subscriptionCardLimit
      });
    }
    return isHigh;
  }, [cardLimit, subscriptionCardLimit]);

  const disabledCreateButton = useMemo(() => loading || isTooHighLimit, [isTooHighLimit, loading]);

  useEffect(() => {
    if (open) {
      const formFieldValues = {
        ...defaultFormValues,
        card_owner: subscriptionOwner
      };
      setInitialFormValues(formFieldValues);
      form.setFieldsValue(formFieldValues);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const options = formHelpers.getEmployeeOptions({employees, employeeEmail, t});
    setOwnerOptions(options);
  }, [employees]); // eslint-disable-line react-hooks/exhaustive-deps

  const trans = (key) => t(`subscriptions:${key}`);

  const handleSubmit = (data) => (handleOk && !isTooHighLimit) && handleOk(data);

  const footer = [
    <Button
      key='submit'
      disabled={disabledCreateButton}
      onClick={() => !disabledCreateButton && form.submit()}
      size='large'
      type='primary'
    >
      {t('create')}
    </Button>
  ];

  const formItemRules = [{required: true, message: t('validation.fieldIsRequired')}];

  return (
    <StyledAddCardModal
      footer={footer}
      open={open}
      onCancel={handleCancel}
      title={`${t('card')} ${t('details')}`}
      width={472}
      {...rest}
    >
      <SpinSmall spinning={loading}>
        <StyledAddCardModalFormSpace
          direction='vertical'
          size='middle'
        >
          <StyledCardInfo align='start'>
            <InfoCircleOutlined />
            {trans('cardCreationDescription')}
          </StyledCardInfo>
          <CardSvgImage />
          <Form
            initialValues={initialFormValues}
            form={form}
            layout='vertical'
            onFinish={handleSubmit}
            requiredMark={false}
          >
            <Item
              label={`${t('card')} ${t('Owner')}`}
              name='card_owner'
              rules={formItemRules}
            >
              <Select
                options={ownerOptions}
                size='large'
              />
            </Item>
            <StyledBudgetDetailsFormSpace size='middle'>
              <Item
                label={`${t('card')} ${t('limit')}`}
                name={cardLimitFieldName}
                rules={formItemRules}
                required
              >
                <EuroInput />
              </Item>
              <Item
                label=' '
                name={cardLimitPeriodFieldName}
                rules={formItemRules}
              >
                <Select
                  options={cardLimitPeriodTypeOptions}
                  size='large'
                />
              </Item>
            </StyledBudgetDetailsFormSpace>
            <Collapse isOpened={isTooHighLimit}>
              <IncreaseLimitAlert
                limit={subscriptionCardLimit}
              />
            </Collapse>
          </Form>
        </StyledAddCardModalFormSpace>
      </SpinSmall>
    </StyledAddCardModal>
  );
}

AddCardModal.propTypes = {
  handleCancel: PropTypes.func,
  handleOk: PropTypes.func,
  loading: PropTypes.bool,
  open: PropTypes.bool,
  subscriptionOwner: PropTypes.string
}

const mapStateToProps = state => {
  const {employees} = state.company;
  const {email: employeeEmail} = state.user.employee;
  const {subscriptionCardLimit} = cardsHelpers.getPaymentCardLimit(state);
  return {
    employees,
    employeeEmail,
    subscriptionCardLimit
  }
}

export default connect(mapStateToProps, null)(AddCardModal);
